import { save } from "@tauri-apps/plugin-dialog";
import { invoke, tauriOnlyInvoke } from "../invoke";
import { saveDialogDefaultPath } from "../dialogDefaults";
import { toMaybe, just, nothing, type Maybe } from "../maybe";
import { resolveHomeDir } from "./dialogHome";
import { isCloudJoinedPath, isDemoJoinedPath } from "../platform/projectRoot";
import { stripSceneFrontmatterLayers } from "../platform/sceneFrontmatter";
import { isTauri } from "../runtime";
import { normalizeZwConfig, type ZwConfig } from "../zwConfig";

export type WindowKind = "splash" | "editor" | "about" | "unknown" | "web";

export type EditorKind = "project";

/** Which of the 5 router tabs is showing. */
export type EditorActiveTab =
  "manuscript" | "storyboard" | "notes" | "database" | "trash";

/** All 5 router tabs, in the same order as `routes.ts`'s child routes —
 * reused wherever code needs to check "is this route name one of the 5 tabs"
 * (e.g. `EditorShell.vue`'s navigation guard) without duplicating the list. */
export const EDITOR_ACTIVE_TABS: readonly EditorActiveTab[] = [
  "manuscript",
  "storyboard",
  "notes",
  "database",
  "trash",
];

export function isEditorActiveTab(value: unknown): value is EditorActiveTab {
  return (EDITOR_ACTIVE_TABS as readonly unknown[]).includes(value);
}

/** The 3 tabs that show file content in the shared editor surface — the
 * other 2 (storyboard/trash) never read `activePath`/`doc`. Exhaustively
 * checked against `EditorActiveTab` wherever this matters (see
 * `EditorShell.vue`'s tab-content resolver) so adding a 6th tab without
 * deciding whether it's file-based is a compile error, not a runtime leak. */
export const EDITOR_FILE_TABS = ["manuscript", "notes", "database"] as const;
export type EditorFileTab = (typeof EDITOR_FILE_TABS)[number];

export function isEditorFileTab(tab: EditorActiveTab): tab is EditorFileTab {
  return (EDITOR_FILE_TABS as readonly string[]).includes(tab);
}

/**
 * App-facing editor session snapshot. Nullable wire fields are Maybe here;
 * convert at `normalizePayload` (invoke / zw-online) and never leak `null`
 * into Vue/Pinia from this type.
 */
export type EditorPayload = {
  kind: EditorKind;
  rootPath: Maybe<string>;
  activePath: Maybe<string>;
  content: string;
  title: string;
  project: Maybe<ZwConfig>;
  /** Active scene's own synopsis (from its frontmatter). Empty when the active
   * file isn't a scene. */
  synopsis: string;
  /** Which of the 5 router tabs is showing; Nothing means unresolved yet.
   * Top-level sibling of `activePath`, not nested in `project`. Kept off
   * ZwConfig (rendering decision, not shared project content). */
  activeTab: Maybe<EditorActiveTab>;
};

/** Wire shape from Tauri / zw-online / demo (serde uses null). */
export type RawEditorPayload = {
  kind: string;
  rootPath: string | null;
  activePath: string | null;
  content: string;
  title: string;
  project?: unknown;
  synopsis?: string;
  activeTab?: string | null;
};

/** IPC boundary: RawEditorPayload (null) → EditorPayload (Maybe). */
export function normalizePayload(raw: RawEditorPayload): EditorPayload {
  const activeTab = isEditorActiveTab(raw.activeTab) ? raw.activeTab : null;
  return {
    kind: "project",
    rootPath: toMaybe(raw.rootPath),
    activePath: toMaybe(raw.activePath),
    content: raw.content,
    title: raw.title,
    project:
      raw.project !== null ? just(normalizeZwConfig(raw.project)) : nothing,
    synopsis: typeof raw.synopsis === "string" ? raw.synopsis : "",
    activeTab: toMaybe(activeTab),
  };
}

export { isTauri, joinProjectPath } from "../runtime";

export async function getWindowKind(): Promise<WindowKind> {
  if (isTauri() === false) return "web";
  return tauriOnlyInvoke<WindowKind>("get_window_kind");
}

/** True when running the single-webview Android/iOS shell. */
export async function isMobileShell(): Promise<boolean> {
  if (isTauri() === false) return false;
  try {
    return await tauriOnlyInvoke<boolean>("is_mobile_shell");
  } catch {
    return false;
  }
}

export const NAVIGATE_EVENT = "zw-navigate";

export type NavigatePayload = { kind: WindowKind };

/** Leave the editor and return to the splash library (mobile shell). */
export async function navigateToSplash(): Promise<void> {
  await tauriOnlyInvoke("navigate_to_splash");
}

export async function getEditorPayload(): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("get_editor_payload");
  return normalizePayload(raw);
}

/** Absolute-path file write — desktop filesystem only, no `zw-online`
 * equivalent (its workspace has no notion of a caller-chosen absolute path). */
export async function writeFile(path: string, content: string): Promise<void> {
  await tauriOnlyInvoke("write_file", { path, content });
}

export async function readProjectFile(path: string): Promise<string> {
  if (isDemoJoinedPath(path)) {
    return invoke<string>("read_project_file", { path });
  }
  if (isCloudJoinedPath(path)) {
    throw new Error("Cloud project files are not read directly yet");
  }
  return tauriOnlyInvoke<string>("read_project_file", { path });
}

export async function saveActive(
  content: string,
  synopsis: string,
): Promise<EditorPayload> {
  const { body } = stripSceneFrontmatterLayers(content);
  const raw = await invoke<RawEditorPayload>("save_active", {
    content: body,
    synopsis,
  });
  return normalizePayload(raw);
}

/** Native "Save As" dialog — desktop only. */
export async function saveAs(
  content: string,
  defaultName = "Untitled.zwdoc",
): Promise<EditorPayload | null> {
  const home = await resolveHomeDir();
  const path = await save({
    title: "Save As",
    defaultPath: saveDialogDefaultPath(home, defaultName),
    filters: [{ name: "Zephyrwrite document", extensions: ["zwdoc"] }],
  });
  if (path === null || path === "") return null;
  const raw = await tauriOnlyInvoke<RawEditorPayload>("save_as", {
    path,
    content,
  });
  return normalizePayload(raw);
}

export async function saveOrSaveAs(
  content: string,
  synopsis = "",
): Promise<EditorPayload | null> {
  try {
    return await saveActive(content, synopsis);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes("NO_PATH")) {
      return saveAs(content);
    }
    throw error;
  }
}
